import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, Info, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, StatCard, StatusBadge, fmtUSD, fmtDate } from "@/components/ui-bits";
import { Field } from "@/pages/Login";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const WEEKEND_NOTICE = "Withdrawal will be credited only on Saturday or Sunday.";

export default function Withdrawals() {
  const { user, refresh } = useAuth();
  const [settings, setSettings] = useState(null);
  const [rows, setRows] = useState([]);
  const [funding, setFunding] = useState([]);
  const [rewardAmount, setRewardAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [otpModal, setOtpModal] = useState(null); // { type, amount?, planId? }
  const [otpCode, setOtpCode] = useState("");

  const load = async () => {
    const [s, w, f] = await Promise.all([api.get("/settings/public"), api.get("/withdrawals/mine"), api.get("/funding/mine")]);
    setSettings(s.data); setRows(w.data); setFunding(f.data);
    refresh();
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sendOtp = async () => {
    const { data } = await api.post("/withdrawals/send-otp");
    return data;
  };

  const openOtp = async (target) => {
    if (!user?.wallet_address) return toast.error("Add a BEP20 wallet address first");
    setBusy(true);
    try {
      const data = await sendOtp();
      setOtpCode("");
      setOtpModal(target);
      if (data?.email_sent) toast.success("Verification code sent to your email");
      else toast.warning(data?.message || "Couldn't send the code — tap Resend in a moment");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const reqRewards = async () => {
    if (!rewardAmount || Number(rewardAmount) <= 0) return toast.error("Enter a valid amount");
    await openOtp({ type: "rewards", amount: Number(rewardAmount) });
  };
  const reqFundingPlan = async (planId) => {
    await openOtp({ type: "funding", planId });
  };

  const confirmOtp = async () => {
    if (otpCode.trim().length !== 6) return toast.error("Enter the 6-digit code");
    setBusy(true);
    try {
      if (otpModal.type === "rewards") {
        await api.post("/withdrawals/rewards", { amount: otpModal.amount, otp: otpCode.trim() });
      } else {
        await api.post("/withdrawals/funding", { plan_id: otpModal.planId, otp: otpCode.trim() });
      }
      toast.success("Withdrawal requested");
      setOtpModal(null); setRewardAmount(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const resendOtp = async () => {
    try { await sendOtp(); toast.success("New code sent"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const deductPct = (settings?.reward_funding_deduction_pct ?? 0.5) * 100;
  const activePlans = funding.filter((f) => f.status === "active");
  const rewardRows = rows.filter((r) => r.type === "rewards");
  const fundingRows = rows.filter((r) => r.type === "funding");

  return (
    <div>
      <PageHeader title="Withdrawals" subtitle={`Minimum reward withdrawal $${settings?.min_withdraw ?? 20}. Withdrawals are sent to your saved BEP20 wallet.`} />

      <div className="glass-strong p-4 mb-5 flex items-start gap-3 border border-[#FFC400]/30 bg-[#FFC400]/5" data-testid="weekend-notice">
        <CalendarClock className="text-[#FFC400] mt-0.5 flex-shrink-0" size={20} />
        <div className="text-sm text-[#FFC400] font-semibold">{WEEKEND_NOTICE}</div>
      </div>

      {!user?.wallet_address && (
        <div className="glass p-4 mb-5 text-sm text-[#94A3B8]">
          You haven't added a withdrawal wallet yet. Please add a BEP20 address in <span className="text-[#00F0FF]">Wallet</span> before requesting a withdrawal.
        </div>
      )}

      <Tabs defaultValue="rewards">
        <TabsList className="bg-[#0A132A] border border-white/10">
          <TabsTrigger value="rewards" data-testid="tab-rewards">Reward Withdrawals</TabsTrigger>
          <TabsTrigger value="funding" data-testid="tab-funding">Funding Withdrawals</TabsTrigger>
        </TabsList>

        <TabsContent value="rewards">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 my-5">
            <div className="glass-strong p-6 neon-glow">
              <StatCard label="Reward Balance" value={fmtUSD(user?.rewards_balance)} />
              <div className="mt-4 space-y-3">
                <Field label="Amount" type="number" value={rewardAmount} onChange={setRewardAmount} testid="wd-rewards-amount" />
                <button onClick={reqRewards} disabled={busy} data-testid="wd-rewards-submit"
                        className="w-full py-3 rounded-lg bg-[#00F0FF] text-[#060B19] font-bold hover:bg-[#00D4E0] transition-all disabled:opacity-50">
                  Request Reward Withdrawal
                </button>
              </div>
              <div className="mt-4 flex items-start gap-2 text-xs text-[#94A3B8] border-t border-white/5 pt-4">
                <Info size={14} className="text-[#00F0FF] mt-0.5 flex-shrink-0" />
                <span>Withdrawing a reward deducts <strong className="text-white">{deductPct}%</strong> of that amount from your withdrawable funding. Reward generation continues on your original contribution amount.</span>
              </div>
            </div>
            <div className="glass p-5 lg:col-span-2">
              <h3 className="font-display text-lg font-bold mb-4">Reward Withdrawal History</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[11px] uppercase tracking-wider text-[#475569]">
                    <tr className="border-b border-white/5"><th className="text-left py-2">Date</th><th className="text-right">Amount</th><th className="text-left pl-4">Txn ID</th><th className="text-right">Status</th></tr>
                  </thead>
                  <tbody data-testid="reward-wd-history">
                    {rewardRows.map((w) => (
                      <tr key={w.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                        <td className="py-3 text-[#94A3B8]">{fmtDate(w.created_at)}</td>
                        <td className="text-right font-mono">{fmtUSD(w.amount)}</td>
                        <td className="pl-4 font-mono text-xs text-[#00F0FF] truncate max-w-[160px]">{w.transaction_id || "—"}</td>
                        <td className="text-right"><StatusBadge status={w.status} /></td>
                      </tr>
                    ))}
                    {rewardRows.length === 0 && <tr><td colSpan={4} className="py-8 text-center text-[#475569]">No reward withdrawals</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="funding">
          <div className="my-5 space-y-5">
            <div className="glass p-5">
              <h3 className="font-display text-lg font-bold mb-1">Withdraw by Plan</h3>
              <p className="text-xs text-[#94A3B8] mb-4">You can only withdraw the <strong className="text-white">full withdrawable amount</strong> of an active plan. Partial withdrawals are not allowed.</p>
              <div className="space-y-3">
                {activePlans.length === 0 && <div className="text-[#475569] text-sm py-6 text-center">No active plans available for withdrawal</div>}
                {activePlans.map((p) => (
                  <div key={p.id} className="border border-white/10 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid={`wd-plan-${p.id}`}>
                    <div className="grid grid-cols-3 gap-4 flex-1 text-xs">
                      <div>
                        <div className="text-[#475569] uppercase tracking-wider">Plan Amount</div>
                        <div className="font-mono text-white text-sm mt-0.5">{fmtUSD(p.plan_amount ?? p.amount)}</div>
                      </div>
                      <div>
                        <div className="text-[#475569] uppercase tracking-wider">Withdrawable</div>
                        <div className="font-mono text-[#00F0FF] text-sm mt-0.5">{fmtUSD(p.withdrawable_amount ?? p.amount)}</div>
                      </div>
                      <div>
                        <div className="text-[#475569] uppercase tracking-wider">Status</div>
                        <div className="mt-0.5"><StatusBadge status={p.status} /></div>
                      </div>
                    </div>
                    <button
                      onClick={() => reqFundingPlan(p.id)}
                      disabled={busy || Number(p.withdrawable_amount ?? p.amount) <= 0}
                      data-testid={`wd-funding-${p.id}`}
                      className="px-4 py-2.5 rounded-lg bg-[#00F0FF] text-[#060B19] font-bold hover:bg-[#00D4E0] transition-all disabled:opacity-40 whitespace-nowrap text-sm"
                    >
                      Withdraw {fmtUSD(p.withdrawable_amount ?? p.amount)}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="glass p-5">
              <h3 className="font-display text-lg font-bold mb-4">Funding Withdrawal History</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[11px] uppercase tracking-wider text-[#475569]">
                    <tr className="border-b border-white/5"><th className="text-left py-2">Date</th><th className="text-right">Amount</th><th className="text-left pl-4">Txn ID</th><th className="text-right">Status</th></tr>
                  </thead>
                  <tbody data-testid="funding-wd-history">
                    {fundingRows.map((w) => (
                      <tr key={w.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                        <td className="py-3 text-[#94A3B8]">{fmtDate(w.created_at)}</td>
                        <td className="text-right font-mono">{fmtUSD(w.amount)}</td>
                        <td className="pl-4 font-mono text-xs text-[#00F0FF] truncate max-w-[160px]">{w.transaction_id || "—"}</td>
                        <td className="text-right"><StatusBadge status={w.status} /></td>
                      </tr>
                    ))}
                    {fundingRows.length === 0 && <tr><td colSpan={4} className="py-8 text-center text-[#475569]">No funding withdrawals</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!otpModal} onOpenChange={(o) => !o && setOtpModal(null)}>
        <DialogContent className="bg-[#0A132A] border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><ShieldCheck size={18} className="text-[#00F0FF]" /> Verify Withdrawal</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#94A3B8]">
              We've sent a 6-digit verification code to <span className="text-white">{user?.email}</span>. Enter it below to confirm your
              {otpModal?.type === "rewards" ? ` reward withdrawal of ${fmtUSD(otpModal?.amount)}` : " funding withdrawal"}.
            </p>
            <Field label="Verification Code" value={otpCode} onChange={(v) => setOtpCode(v.replace(/\D/g, "").slice(0, 6))} testid="wd-otp-code" />
            <button onClick={confirmOtp} disabled={busy} data-testid="wd-otp-confirm"
              className="w-full py-3 rounded-lg bg-[#00F0FF] text-[#060B19] font-bold hover:bg-[#00D4E0] transition-all disabled:opacity-50">
              {busy ? "Verifying…" : "Confirm Withdrawal"}
            </button>
            <button onClick={resendOtp} disabled={busy} data-testid="wd-otp-resend" className="w-full text-sm text-[#00F0FF] hover:underline">Resend code</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
